import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../api";
import { ArrowLeft, CheckCircle2, FileText, PlayCircle, BookOpen } from "lucide-react";

export default function LMSLesson() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get(`/lms/lessons/${lessonId}`); setLesson(r.data); }
    catch (e) { setMsg(formatError(e)); }
  }, [lessonId]);
  useEffect(() => { load(); }, [load]);

  const markComplete = async () => {
    try {
      await api.post(`/lms/lessons/${lessonId}/complete`);
      load();
    } catch (e) { setMsg(formatError(e)); }
  };

  if (!lesson) return <div className="min-h-screen bg-paper p-10 text-muted">{msg || "Loading…"}</div>;
  const Icon = lesson.type === "video" ? PlayCircle : lesson.type === "pdf" ? FileText : BookOpen;

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-rule bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-signal" data-testid="lesson-back">
            <ArrowLeft size={14} /> Back
          </button>
          <Link to={`/student/lms`} className="link-underline text-sm">My Labs</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-center gap-3 mb-3">
          <Icon size={18} className="text-signal" />
          <span className="eyebrow">{lesson.type} · {lesson.duration_min}m</span>
        </div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl leading-tight" data-testid="lesson-title">{lesson.title}</h1>

        <div className="mt-8">
          {lesson.type === "video" && lesson.video_url && (
            <div className="card overflow-hidden" style={{ aspectRatio: "16/9" }} data-testid="lesson-video">
              <iframe title={lesson.title} src={lesson.video_url} className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen />
            </div>
          )}
          {lesson.type === "pdf" && lesson.pdf_url && (
            <a href={lesson.pdf_url} target="_blank" rel="noreferrer" className="card card-pad block hover:border-signal transition" data-testid="lesson-pdf">
              <div className="flex items-center gap-3">
                <FileText size={24} className="text-signal" />
                <div>
                  <div className="font-semibold">Open reference handbook</div>
                  <div className="text-xs text-muted">{lesson.pdf_url}</div>
                </div>
              </div>
            </a>
          )}
          {lesson.content && (
            <article className="card card-pad mt-6 prose-sm" data-testid="lesson-content">
              {lesson.content.split("\n").map((line, i) => {
                const k = `ln-${i}-${line.length}`;
                if (line.startsWith("## ")) return <h3 key={k} className="font-display font-bold text-xl mt-4 mb-2">{line.slice(3)}</h3>;
                if (line.startsWith("### ")) return <h4 key={k} className="font-display font-semibold text-lg mt-4 mb-1">{line.slice(4)}</h4>;
                if (line.startsWith("- ")) return <li key={k} className="ml-5 text-sm leading-relaxed">{line.slice(2)}</li>;
                if (!line.trim()) return <br key={k} />;
                return <p key={k} className="text-sm leading-relaxed mt-2">{line}</p>;
              })}
            </article>
          )}
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-3 border-t border-rule pt-6">
          <div className="text-sm text-muted" data-testid="lesson-msg">{msg}</div>
          {lesson.completed ? (
            <span className="inline-flex items-center gap-2 text-signal font-semibold" data-testid="lesson-completed-pill">
              <CheckCircle2 size={16} /> Lesson completed
            </span>
          ) : (
            <button onClick={markComplete} className="btn-primary" data-testid="lesson-complete-btn">
              Mark as complete
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
